import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../../assets/css/mainMenu.css";
import "../../assets/css/addunit.css";

import fetchWrapper from "./../../AuthMiddleware/FetchWrapper";
import Loader from "../../Components/LoaderAndSpinner/Loader";

const TagReview = () => {
    const [templateStructure, setTemplateStructure] = useState({});
    const [templates, setTemplates] = useState([]);
    const [svps, setSvps] = useState([]);
    const [template, setTemplate] = useState({});
    const [nestedData, setNestedData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const { lineage_id } = useParams();
    const [loading, setLoading] = useState(false);
    const [selectedSvpCode, setSelectedSvpCode] = useState("");
    const navigate = useNavigate();
    const [matchedNodeId, setMatchedNodeId] = useState("");

    useEffect(() => {
        const getTemplates = async () => {
            try {
                const data = await fetchWrapper(
                    `${process.env.REACT_APP_BASE_API_URL1}/api/v1/templates/type/${lineage_id}`,
                    {
                        method: "POST",
                        body: JSON.stringify({}),
                    }
                );
                setTemplates(data.templates || []);
            } catch (error) {
                console.error("Error fetching templates:", error.message);
                if (error.message === "Unauthorized") {
                    navigate("/auth/login");
                }
            }
        };
        getTemplates();
    }, [navigate, lineage_id]);

    useEffect(() => {
        const getSvps = async () => {
            try {
                const svpData = await fetchWrapper(
                    `${process.env.REACT_APP_BASE_API_URL1}/api/v1/get-svp`,
                    {
                        method: "POST",
                        body: JSON.stringify({}),
                    }
                );

                const response = await fetch(
                    `${process.env.REACT_APP_BASE_API_URL1}/api/v1/get-masters`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ masterId: svpData._id }),
                    }
                );

                const result = await response.json();
                setSvps(result);
            } catch (error) {
                console.error("Error fetching SVPs:", error.message);
                if (error.message === "Unauthorized") {
                    navigate("/auth/login");
                }
            }
        };
        getSvps();
    }, [navigate]);

    const fetchMasterData = async (templateId, nodeId, parentData = null) => {
        try {
            const selectedTemplate = templates.find((template) => template._id === templateId);
            if (!selectedTemplate) return;

            setTemplate(selectedTemplate);

            const response = await fetchWrapper(
                `${process.env.REACT_APP_BASE_API_URL1}/api/v1/template-asset-masters/${templateId}`,
                {
                    method: "POST",
                    body: JSON.stringify({ data: parentData }),
                    headers: { "Content-Type": "application/json" },
                }
            );

            const data = response.data || [];
            if (parentData) {
                const updateNested = (items) =>
                    items.map((item) =>
                        item._id === parentData._id
                            ? { ...item, nested: data }
                            : item.nested
                                ? { ...item, nested: updateNested(item.nested) }
                                : item
                    );
                setNestedData((prev) => updateNested(prev));
            } else {
                setNestedData(data);
            }
        } catch (error) {
            console.error("Error fetching master data:", error.message);
        }
    };

    const fetchEquipments = async (assetId, parentItem) => {
        try {
            const response = await fetchWrapper(
                `${process.env.REACT_APP_BASE_API_URL1}/api/v1/asset-equipments/${assetId}`,
                {
                    method: "POST",
                    body: JSON.stringify({}),
                }
            );

            const data = response.data || [];
            const updateNested = (items) =>
                items.map((item) =>
                    item._id === parentItem._id
                        ? { ...item, nested: data }
                        : item.nested
                            ? { ...item, nested: updateNested(item.nested) }
                            : item
                );

            setNestedData((prev) => updateNested(prev));
        } catch (error) {
            console.error("Error fetching equipment data:", error.message);
        }
    };

    const handleRowClick = async (item) => {
        const isExpanded = !!expandedRows[item._id];
        const updatedRows = { ...expandedRows };

        if (isExpanded) {
            const collapse = (items) =>
                items.forEach((child) => {
                    if (updatedRows[child._id]) {
                        updatedRows[child._id] = false;
                        if (child.nested?.length) collapse(child.nested);
                    }
                });
            collapse(item.nested || []);
        }

        updatedRows[item._id] = !isExpanded;
        setExpandedRows(updatedRows);

        if (!isExpanded) {
            const nextNode = findNextNode(item.master_id);
            if (nextNode) {
                if (item.leaf_node) {
                    await fetchEquipments(item.asset_id, item);
                } else {
                    await fetchMasterData(item.template_id, nextNode.id, item);
                }
            } else {
                await fetchEquipments(item.asset_id, item);
            }
        }
    };

    const findNextNode = (currentNodeId) => {
        const nodes = templateStructure?.[0]?.nodes || [];
        const index = nodes.findIndex((n) => n.id === currentNodeId);
        return index >= 0 && index + 1 < nodes.length ? nodes[index + 1] : null;
    };

    const renderNestedData = (items, level = 0) => (
        <>
            {items.map((item) => {
                if (!item.document_code && !item.equipment_code) return null;

                return (
                    <React.Fragment key={item._id}>
                        <tr>
                            <td
                                onClick={() => {
                                    if (item.document_code) {
                                        handleRowClick(item);
                                    }
                                }}
                                className="col"
                                style={{
                                    paddingLeft: `${20 * level}px`,
                                    cursor: item.document_code ? "pointer" : "default",
                                    fontStyle: "normal",
                                }}
                            >
                                {item.document_code ? (
                                    <>
                                        {expandedRows[item._id] ? (
                                            <span style={{ marginRight: "8px" }}>-</span>
                                        ) : (
                                            <span style={{ marginRight: "8px" }}>+</span>
                                        )}
                                        {item.document_code}
                                    </>
                                ) : (
                                    `${item.equipment_code} - ${item.equipment_name}`
                                )}
                            </td>

                            {/* ✅ Show checkbox only for template (document_code exists) */}
                            <td className="col text-center">
                                {item.document_code && (
                                    item.master_id === matchedNodeId ? (
                                        <input type="checkbox" readOnly checked />
                                    ) : (
                                        <span style={{ color: "red", fontWeight: "bold" }}>✖</span>
                                    )
                                )}
                            </td>
                        </tr>

                        {expandedRows[item._id] &&
                            Array.isArray(item.nested) &&
                            item.nested.length > 0 &&
                            renderNestedData(item.nested, level + 1)}
                    </React.Fragment>
                );
            })}
        </>
    );


    return (
        <>
            {loading ? (
                <Loader />
            ) : (
                <div className="container">
                    <div className="pt-3 pb-0 d-flex justify-content-between">
                        <div>
                            <h5>Tag Review</h5>
                            <nav className="breadcrumb-nav" aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="#">Review</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Tag Review</li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex gap-3">
                            <select
                                style={{ padding: "10px", width: "250px" }}
                                className="select-unit textarea"
                                onChange={(e) => fetchMasterData(e.target.value, null)}
                            >
                                <option value="">Select Template</option>
                                {templates.map((template) => (
                                    <option key={template._id} value={template._id}>
                                        {template.template_name}
                                    </option>
                                ))}
                            </select>
                            <select
                                style={{ padding: "10px", width: "250px" }}
                                className="select-unit textarea"
                                onChange={(e) => {
                                    const svpId = e.target.value;
                                    const selectedSvp = svps.find((svp) => svp._id === svpId);
                                    setSelectedSvpCode(selectedSvp?.var_prcs_code || selectedSvp?.var_prcs_txt || "");

                                    let matchFound = false;

                                    for (const temp of templates) {
                                        try {
                                            if (!temp.structure) continue;
                                            const parsedStructure = JSON.parse(temp.structure);
                                            const nodeMatch = parsedStructure?.[0]?.nodes?.find(node => node.id === svpId);

                                            if (nodeMatch) {

                                                setMatchedNodeId(nodeMatch.id);
                                                setTemplate(temp);
                                                setTemplateStructure(parsedStructure);
                                                fetchMasterData(temp._id, null); // load data
                                                matchFound = true;
                                                break;
                                            }
                                        } catch (err) {
                                            console.error(" Error parsing template structure:", err.message);
                                        }
                                    }

                                    if (!matchFound) {
                                        console.warn(" No SVP match found. Still loading data.");
                                        setMatchedNodeId("");
                                        const defaultTemplate = templates[0];
                                        if (defaultTemplate) {
                                            setTemplate(defaultTemplate);
                                            fetchMasterData(defaultTemplate._id, null);
                                        }
                                    }
                                }}
                            >
                                <option value="">Select SVP</option>
                                {svps.map((svp) => (
                                    <option key={svp._id} value={svp._id}>
                                        {svp.var_prcs_code || svp.var_prcs_txt}
                                    </option>
                                ))}
                            </select>


                        </div>
                    </div>

                    <div className="table-responsive mt-3">
                        <table className="table align-middle table-text">
                            {/* <thead className="table-head">
                                <tr>
                                    <th className="col text-uppercase">Asset Master Code</th>
                                </tr>
                            </thead> */}
                            <thead className="table-head align-middle">
                                <tr>
                                    <th className="col text-uppercase">Asset Master Code</th>
                                    <th className="col text-uppercase text-center">SPV</th> {/* ✅ New column */}
                                </tr>
                            </thead>
                            <tbody>
                                {!Array.isArray(nestedData) || nestedData.length === 0 ? (
                                    <tr>
                                        <td colSpan="100%" className="text-center">
                                            No data available
                                        </td>
                                    </tr>
                                ) : (
                                    renderNestedData(nestedData)
                                )}
                            </tbody>
                        </table>
                    </div>

                    <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
                </div>
            )}
        </>
    );
};

export default TagReview;
